import { create } from 'zustand';
import { MundusStore, CreateMundusInput, Mundus } from '../types/mundus';
import api from '../lib/api';
import { apiCache, cacheKeys, cacheTTL } from '../lib/cache';

export const useMundusStore = create<MundusStore>((set) => ({
  // Initial state
  munduses: [],
  currentMundus: null,
  templates: [],
  isLoading: false,
  error: null,

  // Actions
  fetchTemplates: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.get('/api/mundus/templates');
      const templates = response.data.data;

      set({
        templates,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch templates';
      set({
        isLoading: false,
        error: errorMessage,
      });
    }
  },

  fetchMunduses: async () => {
    try {
      set({ isLoading: true, error: null });

      // Use cache for munduses list
      const munduses = await apiCache.withCache(
        'munduses:list',
        async () => {
          const response = await api.get('/api/mundus');
          return response.data.data;
        },
        cacheTTL.medium
      );

      set({
        munduses,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Munduses';
      set({
        isLoading: false,
        error: errorMessage,
      });
    }
  },

  fetchMundusById: async (id: string) => {
    try {
      set({ isLoading: true, error: null });

      // Use cache for individual mundus
      const mundus = await apiCache.withCache(
        cacheKeys.mundus(id),
        async () => {
          const response = await api.get(`/api/mundus/${id}`);
          return response.data.data;
        },
        cacheTTL.medium
      );

      set({
        currentMundus: mundus,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch Mundus';
      set({
        isLoading: false,
        error: errorMessage,
      });
    }
  },

  createMundus: async (input: CreateMundusInput): Promise<Mundus> => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.post('/api/mundus', input);
      const mundus = response.data.data;

      // Invalidate munduses list cache
      apiCache.invalidate('munduses:list');

      // Add to munduses list
      set((state) => ({
        munduses: [mundus, ...state.munduses],
        isLoading: false,
        error: null,
      }));

      return mundus;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create Mundus';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw new Error(errorMessage);
    }
  },

  updateMundus: async (id: string, data: Partial<Mundus>) => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.put(`/api/mundus/${id}`, data);
      const updatedMundus = response.data.data;

      // Invalidate caches
      apiCache.invalidate(cacheKeys.mundus(id));
      apiCache.invalidate('munduses:list');

      // Update in munduses list
      set((state) => ({
        munduses: state.munduses.map((m) => (m.id === id ? updatedMundus : m)),
        currentMundus: state.currentMundus?.id === id ? updatedMundus : state.currentMundus,
        isLoading: false,
        error: null,
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update Mundus';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw new Error(errorMessage);
    }
  },

  deleteMundus: async (id: string) => {
    try {
      set({ isLoading: true, error: null });

      await api.delete(`/api/mundus/${id}`);

      // Invalidate caches
      apiCache.invalidate(cacheKeys.mundus(id));
      apiCache.invalidate('munduses:list');

      // Remove from munduses list
      set((state) => ({
        munduses: state.munduses.filter((m) => m.id !== id),
        currentMundus: state.currentMundus?.id === id ? null : state.currentMundus,
        isLoading: false,
        error: null,
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete Mundus';
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw new Error(errorMessage);
    }
  },

  clearError: () => set({ error: null }),
}));
